import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Image as ImageIcon, Upload, Download, Loader2, X, Sparkles, FileSpreadsheet, Plus, Trash2,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const triggerDownload = (dataUrl, filename) => {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// Parse a CSV blob into [{title, subtitle}]. Header row optional.
// Accepts either "title,subtitle" or just "title" rows. Comma OR semicolon.
const parseCsv = (text) => {
  const rows = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return rows;
  // Detect separator
  const sep = lines[0].includes(";") ? ";" : ",";
  const looksLikeHeader = /^title\b/i.test(lines[0].split(sep)[0].trim());
  for (let i = looksLikeHeader ? 1 : 0; i < lines.length; i++) {
    const cells = lines[i].split(sep).map((c) => c.replace(/^"|"$/g, "").trim());
    if (cells[0]) rows.push({ title: cells[0] || "", subtitle: cells[1] || "" });
  }
  return rows;
};

export const LogoGeneratorPage = () => {
  const iconInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const [iconDataUrl, setIconDataUrl] = useState("");
  const [iconFileName, setIconFileName] = useState("");
  const [logoColor, setLogoColor] = useState("#B8860B");
  const [titleColor, setTitleColor] = useState("#1e293b");
  const [defaultSubtitle, setDefaultSubtitle] = useState("Remodel Contractors Pro");
  const [items, setItems] = useState([{ title: "Louisville Bathroom", subtitle: "Remodel Contractors Pro" }]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const validItems = useMemo(() => items.filter((i) => (i.title || "").trim()), [items]);

  // ---------- Icon upload ----------
  const handleIconUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a PNG or image file.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Icon file too large (max 4MB).");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setIconDataUrl(dataUrl);
    setIconFileName(file.name);
    setPreviewUrl("");
  };

  const onIconChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleIconUpload(f);
  };

  const onIconDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleIconUpload(f);
  };

  // ---------- CSV upload ----------
  const onCsvChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const rows = parseCsv(text);
    if (!rows.length) {
      toast.error("CSV looks empty.");
      return;
    }
    const filled = rows.map((r) => ({ title: r.title, subtitle: r.subtitle || defaultSubtitle }));
    setItems(filled);
    toast.success(`Loaded ${rows.length} rows from CSV.`);
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  // ---------- Items table ----------
  const updateItem = (idx, key, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, { title: "", subtitle: defaultSubtitle }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const applyDefaultSubtitleToAll = () => {
    setItems((prev) => prev.map((it) => ({ ...it, subtitle: defaultSubtitle })));
    toast.success("Default subtitle applied to every row.");
  };

  // ---------- Preview ----------
  const generatePreview = async () => {
    if (!iconDataUrl) return toast.error("Upload an icon first.");
    const first = validItems[0];
    if (!first) return toast.error("Add at least one row with a title.");
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/logos/preview`, {
        icon_data_url: iconDataUrl,
        title: first.title,
        subtitle: first.subtitle || defaultSubtitle,
        logo_color: logoColor,
        title_color: titleColor,
      });
      setPreviewUrl(res.data?.data_url || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // ---------- Bulk ZIP ----------
  const generateBulk = async () => {
    if (!iconDataUrl) return toast.error("Upload an icon first.");
    if (!validItems.length) return toast.error("Add at least one row with a title.");
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/logos/bulk-zip`, {
        icon_data_url: iconDataUrl,
        logo_color: logoColor,
        title_color: titleColor,
        items: validItems,
      });
      const { zip_data_url, filename, count } = res.data || {};
      if (zip_data_url) {
        triggerDownload(zip_data_url, filename || "logos-batch.zip");
        toast.success(`Generated ${count} logos. Download started.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk generation failed.");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="logo-generator-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ImageIcon className="w-7 h-7 text-orange-500" />
            Logo Generator
          </h1>
          <p className="text-slate-600 mt-1">
            Generate transparent 512×512 PNG logos in bulk. Every row reuses the exact logo color and title color you set below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ---- Left panel: settings + items ---- */}
        <div className="lg:col-span-2 space-y-6">
          {/* Icon upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Upload className="w-4 h-4" /> 1. Base icon (transparent PNG)
              </CardTitle>
              <CardDescription>
                Monochrome PNG with transparent background. The icon will be recolored to the logo color you choose below.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onIconDrop}
                className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                onClick={() => iconInputRef.current?.click()}
                data-testid="icon-dropzone"
              >
                <input
                  ref={iconInputRef}
                  type="file"
                  accept="image/png,image/svg+xml"
                  className="hidden"
                  onChange={onIconChange}
                  data-testid="icon-file-input"
                />
                {iconDataUrl ? (
                  <div className="flex items-center justify-center gap-4">
                    <img src={iconDataUrl} alt="icon preview" className="w-20 h-20 object-contain bg-slate-200 rounded" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-900">{iconFileName}</p>
                      <button
                        type="button"
                        className="text-xs text-red-500 hover:underline mt-1"
                        onClick={(e) => { e.stopPropagation(); setIconDataUrl(""); setIconFileName(""); setPreviewUrl(""); }}
                        data-testid="icon-remove-btn"
                      >
                        <X className="w-3 h-3 inline" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">Click or drop a PNG icon here (max 4MB)</p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Colors</CardTitle>
              <CardDescription>
                The subtitle uses the same color as the logo (icon). The title stroke is always white. Every row in the batch uses the exact colors you pick here — no hue shift.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="logo-color">Logo color (icon + subtitle)</Label>
                <div className="flex gap-2 items-center">
                  <Input id="logo-color" type="color" value={logoColor} onChange={(e) => setLogoColor(e.target.value)} className="w-16 h-10 p-1 cursor-pointer" data-testid="logo-color-picker" />
                  <Input value={logoColor} onChange={(e) => setLogoColor(e.target.value)} className="font-mono" data-testid="logo-color-hex" />
                </div>
              </div>
              <div>
                <Label htmlFor="title-color">Title color (fill)</Label>
                <div className="flex gap-2 items-center">
                  <Input id="title-color" type="color" value={titleColor} onChange={(e) => setTitleColor(e.target.value)} className="w-16 h-10 p-1 cursor-pointer" data-testid="title-color-picker" />
                  <Input value={titleColor} onChange={(e) => setTitleColor(e.target.value)} className="font-mono" data-testid="title-color-hex" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">3. Logos to generate</CardTitle>
              <CardDescription>
                Add rows manually or import a CSV with columns <code>title,subtitle</code> (header row optional). Up to 200 logos per batch.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                <div className="md:col-span-1">
                  <Label htmlFor="default-sub">Default subtitle (applied when CSV row has no subtitle)</Label>
                  <Input
                    id="default-sub"
                    value={defaultSubtitle}
                    onChange={(e) => setDefaultSubtitle(e.target.value)}
                    placeholder="Remodel Contractors Pro"
                    data-testid="default-subtitle-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => csvInputRef.current?.click()} data-testid="csv-upload-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> Import CSV
                  </Button>
                  <input ref={csvInputRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onCsvChange} data-testid="csv-file-input" />
                  <Button type="button" variant="outline" size="sm" onClick={applyDefaultSubtitleToAll} data-testid="apply-default-sub-btn">
                    Apply to all
                  </Button>
                </div>
              </div>

              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-row-${idx}`}>
                    <span className="col-span-1 text-xs text-slate-400 text-right">{idx + 1}.</span>
                    <Input className="col-span-5" placeholder="Title (e.g. Louisville Bathroom)" value={it.title} onChange={(e) => updateItem(idx, "title", e.target.value)} data-testid={`item-title-${idx}`} />
                    <Input className="col-span-5" placeholder="Subtitle" value={it.subtitle} onChange={(e) => updateItem(idx, "subtitle", e.target.value)} data-testid={`item-subtitle-${idx}`} />
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(idx)} disabled={items.length <= 1} data-testid={`item-remove-${idx}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add row
              </Button>
              <p className="text-xs text-slate-500">
                {validItems.length} valid row{validItems.length !== 1 ? "s" : ""} ready • Every logo uses the same colors.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* ---- Right panel: preview + actions ---- */}
        <div className="space-y-6">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Preview
              </CardTitle>
              <CardDescription>Renders the first row only. Bulk download runs all valid rows.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="aspect-square rounded-lg border border-slate-200 bg-slate-100 bg-[linear-gradient(45deg,#cbd5e1_25%,transparent_25%,transparent_75%,#cbd5e1_75%),linear-gradient(45deg,#cbd5e1_25%,transparent_25%,transparent_75%,#cbd5e1_75%)] bg-[length:20px_20px] bg-[position:0_0,10px_10px] flex items-center justify-center overflow-hidden">
                {previewUrl ? (
                  <img src={previewUrl} alt="logo preview" className="w-full h-full object-contain" data-testid="logo-preview-img" />
                ) : (
                  <div className="text-center text-slate-500 text-sm p-4">
                    {iconDataUrl ? "Click ‟Generate preview″ to see the first logo." : "Upload an icon to start."}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={generatePreview}
                disabled={!iconDataUrl || !validItems.length || previewLoading}
                data-testid="generate-preview-btn"
              >
                {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate preview
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={generateBulk}
                disabled={!iconDataUrl || !validItems.length || bulkLoading}
                data-testid="generate-bulk-btn"
              >
                {bulkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download ZIP ({validItems.length} logos)
              </Button>
              <p className="text-xs text-slate-400 text-center">512×512 PNG • Transparent background</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">CSV format</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                readOnly
                rows={5}
                className="font-mono text-xs"
                value={`title,subtitle
Louisville Bathroom,Remodel Contractors Pro
Chicago Bathroom,Remodel Contractors Pro
Houston Bathroom,Remodel Contractors Pro`}
              />
              <p className="text-xs text-slate-500 mt-2">
                Header row is optional. Subtitle column is optional — empty subtitles fall back to the default above.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
